import React, { useState, useEffect } from "react";
import { Button, Card } from "react-bootstrap";
import DataTable from "react-data-table-component";
import CreateCityModal from "./CitiesEditModal";
import DeleteCityModal from "./DeleteCityModal";
import { useNavigate } from "react-router-dom";
import CityData from "../../../../../services/city.service";
import { useFormik } from "formik";
import * as Yup from "yup";
import { enqueueSnackbar } from "notistack";
import Switch from "./Switch";

const Cities = () => {
  const [showCreateCityModal, setShowCreateCityModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCityIndex, setSelectedCityIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainData, setMainData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getMainCityData = async () => {
    try {
      setLoading(true);
      const response = await CityData.getCityData();
      setMainData(response.data["data"]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  useEffect(() => {
    getMainCityData();
  }, []);

  const handleFormSubmit = async (values, action) => {
    const payload = { name: values.name, isPublish: values.isPublish };
    if (!values._id) {
      try {
        const response = await CityData.CreateCityData(payload);
        action.resetForm();
        getMainCityData();
        handleCloseCreateCityModal();
        enqueueSnackbar(response.data.message, {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
        action.resetForm();
      } catch (e) {
        enqueueSnackbar(e.response.data.message, {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }
    } else {
      try {
        const response = await CityData.UpdateCityDataRequest(
          values._id,
          values.name,
          values.isPublish
        );
        action.resetForm();
        getMainCityData();
        handleCloseCreateCityModal();
        enqueueSnackbar(response.data.message, {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      } catch (e) {
        enqueueSnackbar(e.response.data.message, {
          variant: "error",
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }
    }
  };

  const navigate = useNavigate();

  const handleCreateCityClick = async (id) => {
    setSelectedCityIndex(null);
    setShowCreateCityModal(true);
  };

  const handleCloseCreateCityModal = () => {
    setShowCreateCityModal(false);
    setSelectedCityIndex(null);
  };

  const handleEditClick = async (id) => {
    setSelectedCityIndex(id);

    setShowCreateCityModal(true);
    if (id) {
      const response = await CityData.GetCitySingleId(id);

      const result = response.data.data;

      formik.setFieldValue("name", result.name);
      formik.setFieldValue("isPublish", result.isPublish);
      formik.setFieldValue("_id", result._id);
    } else {
      formik.setFieldValue("categoryName", "");
      formik.setFieldValue("categoryImage", "");
      formik.resetForm();
    }
  };

  const handleDeleteClick = (_id, name) => {
    setShowDeleteConfirmationModal(true);
    setDeletingIndex(_id);
    setSelectedCityIndex({ id: _id, name: name });
  };

  const handleConfirmDelete = async (id) => {
    try {
      const response = await CityData.DeleteCityDataRequest(id);

      setShowDeleteConfirmationModal(false);

      getMainCityData();
      enqueueSnackbar(response.data.message, {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    } catch (e) {
      enqueueSnackbar("Error deleting city", {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }
  };

  const handleViewDetailsClick = async (_id, name) => {
    navigate(`/cities/facilitise/details/${_id}`, {
      state: {
        cityName: name,
        cityId: _id,
      },
    });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      isPublish: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("City Name is required"),
      isPublish: Yup.boolean(),
    }),
    onSubmit: handleFormSubmit,
  });

  const columns = [
    {
      name: "ID",
      selector: (row) => row._id, 
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) => new Date(row.createdAt).getTime(), 
      sortable: true,
      cell: (row) => new Date(row.createdAt).toLocaleDateString("en-GB"), 
    },
    {
      name: "City Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Number of facilities",
      selector: (row) => `(${row.facilitiesCount})`,
      sortable: true,
    },
    {
      name: "Update Date",
      selector: (row) => {
        const date = new Date(row.updatedAt);
        return date.toLocaleDateString("en-GB");
      },
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            gap: "15px",
          }}
        >
          <Button
            style={{ margin: "5px" }}
            size="sm"
            variant="info"
            onClick={() => handleEditClick(row._id)}
          >
            <i className="fe fe-edit"></i>
          </Button>
          <Button
            style={{ margin: "5px" }}
            size="sm"
            variant="danger"
            onClick={() => handleDeleteClick(row._id, row?.name)}
          >
            <i className="fa fa-trash"></i>
          </Button>
          <Button
            style={{ margin: "5px" }}
            size="sm"
            variant="primary"
            onClick={() => handleViewDetailsClick(row._id, row?.name)}
          >
            <i className="fa fa-eye"></i>
          </Button>
        </div>
      ),
    },
    {
      name: "Publish",
      cell: (row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%", 
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Switch
              value="city"
              type="switch"
              id={`publish-switch-${row._id}`}
              checked={row.isPublish}
              cityID={row?._id}
              cityName={row?.name}
              getMainCityData={getMainCityData}
              style={{
                margin: 0,
                transform: "scale(1.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  const data =
    mainData &&
    mainData.map((city, index) => ({
      _id: city._id,
      name: city.name,
      createdAt: city.createdAt,
      updatedAt: city.updatedAt,
    }));
  // const dataToShow = mainData.length > 0 ? data : [];

  const filteredData = mainData.filter((item) => {
    return (
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item._id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      new Date(item.createdAt)
        .toLocaleDateString("en-GB")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      new Date(item.updatedAt)
        .toLocaleDateString("en-GB")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  });

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const dataToShow = searchQuery ? filteredData : mainData;

  return (
    <>
      <div className="page-header">
        <div>
          <h2 className="main-content-title tx-24 mg-b-5">Cities</h2>
        </div>
        <div className="d-flex">
          <div className="justify-content-center">
            <Button
              variant="primary"
              type="button"
              className="my-2 btn-icon-text"
              onClick={handleCreateCityClick}
              style={{ borderRadius: "5px" }}
            >
              <i className="fe fe-plus me-1"></i>Create City
            </Button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 mb-4">
          <Card>
            <Card.Body style={{ padding: "17px" }}>
              <div className="d-flex mb-3" style={{ float: "right" }}>
                <input
                  type="text"
                  placeholder="Search City..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="form-control"
                  style={{ marginRight: "10px", width: "100%" }}
                />
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setSearchQuery("")}
                  style={{ borderRadius: "5px" }}
                >
                  Clear
                </Button>
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={dataToShow}
                  pagination
                  paginationPerPage={10}
                  progressPending={loading}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <CreateCityModal
        show={showCreateCityModal}
        handleClose={handleCloseCreateCityModal}
        handleSave={formik.handleSubmit}
        isEdit={selectedCityIndex !== null}
        cityData={selectedCityIndex !== null ? cities[selectedCityIndex] : null}
        formik={formik}
      />

      <DeleteCityModal
        show={showDeleteConfirmationModal}
        handleClose={() => setShowDeleteConfirmationModal(false)}
        handleConfirmDelete={handleConfirmDelete}
        _id={deletingIndex}
        cityName={selectedCityIndex ? selectedCityIndex.name : ""}
      />
    </>
  );
};

export default Cities;
